import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { cartApi } from '@/features/cart/api/cartApi';

export const CART_KEY = ['cart'];

/**
 * The basket, and the four things anyone does to it.
 *
 * One hook rather than a context provider: TanStack Query is already the
 * cache, so a second copy of the cart in React state would be a second source
 * of truth to keep in step. The cart page calls this and the badges call
 * useCartCount below, and all of them read the same cached response.
 */
export function useCart() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: CART_KEY,
    queryFn: cartApi.get,
    // The cart is small and changes under the user's own hand, so it is
    // cheap to re-read and confusing to show stale.
    staleTime: 5_000,
  });

  function onSuccess(cart) {
    // The server returns the whole cart, so it is written straight into the
    // cache rather than triggering a refetch of what we already have.
    queryClient.setQueryData(CART_KEY, cart);
  }

  const add = useMutation({
    mutationFn: ({ offerId, quantity = 1 }) => cartApi.addItem(offerId, quantity),
    onSuccess: (cart) => {
      onSuccess(cart);
      toast.success('Added to cart');
    },
    onError: (error) => toast.error(error?.message ?? 'Could not add that item'),
  });

  const setQuantity = useMutation({
    mutationFn: ({ itemId, quantity }) => cartApi.setQuantity(itemId, quantity),
    onSuccess,
    onError: (error) => toast.error(error?.message ?? 'Could not update the quantity'),
  });

  const remove = useMutation({
    mutationFn: (itemId) => cartApi.removeItem(itemId),
    onSuccess: (cart) => {
      onSuccess(cart);
      toast.success('Removed');
    },
    onError: (error) => toast.error(error?.message ?? 'Could not remove that item'),
  });

  const cart = query.data;

  return {
    cart,
    lines: cart?.lines ?? [],
    itemCount: cart?.item_count ?? 0,
    subtotal: cart?.subtotal ?? '0.00',
    isOrderable: cart?.is_orderable ?? false,
    isLoading: query.isLoading,
    add,
    setQuantity,
    remove,
    refresh: () => queryClient.invalidateQueries({ queryKey: CART_KEY }),
  };
}

/**
 * Just the number of items, for the badges in the header and tab bar.
 *
 * Same cache entry as useCart, but a badge can be a minute old: every add,
 * quantity change and removal writes the new cart straight into the cache, so
 * the count only drifts when the basket changes in another tab. The cart and
 * checkout pages keep useCart's 5 seconds.
 */
export function useCartCount() {
  const { data = 0 } = useQuery({
    queryKey: CART_KEY,
    queryFn: cartApi.get,
    staleTime: 60_000,
    select: (cart) => cart?.item_count ?? 0,
  });
  return data;
}
